import {
  Box,
  Button,
  Checkbox,
  Grid,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import EditCommentModal from 'components/modal/crm/EditCommentModal';
import { Small, Tiny } from 'components/Typography';
import { Check, CornerDownRight, Edit3, Trash2 } from 'lucide-react';
import { useState } from 'react';

// CRM 전용 댓글 아이템 (체크박스 + 회색 배경, 메타 정보, 액션 버튼)
// item.replies: 메인 댓글 아래에 붙는 하위 답글 목록
const CrmCommentsItem = ({ item, onClick, checked, onToggle, ...props }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingComment, setEditingComment] = useState(null);
  const [isEditingReply, setIsEditingReply] = useState(false);

  const statusBgColor =
    item.statusLabel === '승인됨'
      ? '#06C755'
      : item.statusLabel === '승인 대기'
      ? '#D9AA00'
      : item.statusLabel === '신고됨'
      ? '#F04438'
      : theme.palette.primary.grey10;

  const handleClick = () => {
    if (onClick) {
      onClick(item);
    }
  };

  const handleEditClick = (commentItem, isReply = false) => {
    setEditingComment(commentItem);
    setIsEditingReply(isReply);
    setShowEditModal(true);
  };

  const handleSaveComment = newText => {
    console.log('댓글 수정:', {
      id: editingComment.id,
      newText,
      isReply: isEditingReply,
    });
  };

  return (
    <>
      <Grid onClick={handleClick} item xs={12} key={item.id} {...props}>
        <Box
          sx={{
            cursor: 'pointer',
            transition: 'all 0.2s ease-in-out',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: theme.palette.primary.grey800,
            borderRadius: '12px',
            padding: '10px 14px',
            border: 'none',
            '&:hover': isMobile
              ? {}
              : {
                  transform: 'translateY(-2px)',
                },
          }}
        >
          <Box display="flex" alignItems="flex-start" gap={1.5}>
            <Checkbox
              size="small"
              checked={checked}
              onChange={e => onToggle?.(item, e.target.checked)}
              sx={{
                mt: 0.5,
                '&.Mui-checked': {
                  color: theme.palette.primary.mainBlue,
                },
              }}
            />

            <Box flex={1} display="flex" flexDirection="column">
              {/* 상단 메타 정보 행 */}
              <Box
                display="flex"
                alignItems="center"
                flexWrap="wrap"
                rowGap={0.8}
                columnGap={1}
                mb={1}
              >
                {/* 상태 배지 */}
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.6,
                    backgroundColor: statusBgColor,
                    color: '#ffffff',
                    px: 1.2,
                    py: 0.3,
                    borderRadius: '999px',
                    fontSize: '12px',
                    fontWeight: 600,
                  }}
                >
                  {item.statusLabel === '승인됨' && (
                    <Check size={14} strokeWidth={3} />
                  )}
                  {item.statusLabel}
                </Box>

                {/* 타입 배지 (댓글 / 답글 등) */}
                <Box
                  sx={{
                    backgroundColor: theme.palette.primary.grey10,
                    color: '#4B4F5C',
                    px: 1.2,
                    py: 0.3,
                    borderRadius: '999px',
                    fontSize: '12px',
                    fontWeight: 500,
                  }}
                >
                  {item.typeLabel}
                </Box>

                {/* 카테고리 배지 (예: 뒷담화) */}
                {item.categoryLabel && (
                  <Box
                    sx={{
                      backgroundColor: theme.palette.primary.grey10,
                      color: '#4B4F5C',
                      px: 1.2,
                      py: 0.3,
                      borderRadius: '999px',
                      fontSize: '12px',
                      fontWeight: 500,
                    }}
                  >
                    {item.categoryLabel}
                  </Box>
                )}

                {/* 댓글 ID 표시 */}
                {item.id && (
                  <Box
                    sx={{
                      backgroundColor: theme.palette.primary.grey10,
                      color: '#4B4F5C',
                      px: 1.0,
                      py: 0.2,
                      borderRadius: '999px',
                      fontSize: '12px',
                      fontWeight: 500,
                    }}
                  >
                    ID {item.id}
                  </Box>
                )}

                {/* 작성자 / 닉네임 */}
                <Small
                  sx={{
                    color: '#151515',
                    fontWeight: 500,
                    mr: 0.5,
                    fontSize: '13px',
                  }}
                >
                  {item.nickname}
                </Small>
                {item.realName && (
                  <Tiny sx={{ color: theme.palette.primary.greytxt }}>
                    ({item.realName})
                  </Tiny>
                )}

                {/* 오른쪽 정렬 메타 (날짜, IP) */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    ml: 'auto',
                    flexWrap: 'wrap',
                  }}
                >
                  <Box
                    sx={{
                      backgroundColor: theme.palette.primary.grey10,
                      borderRadius: '999px',
                      px: 1.2,
                      py: 0.3,
                    }}
                  >
                    <Tiny
                      sx={{
                        color: '#4B4F5C',
                        fontWeight: 500,
                        fontSize: '12px',
                      }}
                    >
                      {item.date}
                    </Tiny>
                  </Box>
                  <Box
                    sx={{
                      backgroundColor: theme.palette.primary.grey10,
                      borderRadius: '999px',
                      px: 1.2,
                      py: 0.3,
                    }}
                  >
                    <Tiny
                      sx={{
                        color: '#4B4F5C',
                        fontWeight: 500,
                        fontSize: '12px',
                      }}
                    >
                      IP : {item.ip}
                    </Tiny>
                  </Box>
                </Box>
              </Box>

              {/* 상단 게시글 제목 (첫 행 등 필요 시) */}
              {item.postTitle && (
                <Box mb={1}>
                  <Tiny
                    sx={{
                      color: theme.palette.primary.greytxt,
                      fontSize: '12px',
                    }}
                  >
                    게시글 : {item.postTitle}
                  </Tiny>
                </Box>
              )}

              {/* 메인 댓글 본문 영역 (왼쪽 인덴트 아이콘 없이) */}
              <Box>
                <Box
                  sx={{
                    backgroundColor: '#ffffff',
                    borderRadius: '12px',
                    px: 2,
                    py: 1.5,
                    boxShadow: '0px 2px 5px rgba(0, 0, 0, 0.05)',
                  }}
                >
                  <Small
                    sx={{
                      color: theme.palette.text.primary,
                      lineHeight: 1.5,
                      fontSize: isMobile ? '13px' : '14px',
                    }}
                  >
                    {item.commentText}
                  </Small>

                  {/* 액션 버튼들 */}
                  <Box
                    mt={1.5}
                    display="flex"
                    alignItems="center"
                    gap={1}
                    flexWrap="wrap"
                  >
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<Edit3 size={16} />}
                      onClick={e => {
                        e.stopPropagation();
                        handleEditClick(item, false);
                      }}
                      sx={{
                        backgroundColor: theme.palette.primary.mainBlue,
                        color: '#ffffff',
                        borderRadius: '8px',
                        px: isMobile ? 1.5 : 2,
                        py: isMobile ? 0.8 : 0.5,
                        fontSize: '13px',
                        fontWeight: 600,
                        textTransform: 'none',
                        boxShadow: 'none',
                        flex: isMobile ? 1 : 'initial',
                        whiteSpace: 'nowrap',
                        '& .lucide': {
                          width: 16,
                          height: 16,
                        },
                        '&:hover': {
                          backgroundColor: theme.palette.primary.mainBlueHover,
                          boxShadow: 'none',
                        },
                      }}
                    >
                      수정
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<Trash2 size={16} />}
                      sx={{
                        borderRadius: '8px',
                        px: isMobile ? 1.5 : 2,
                        py: isMobile ? 0.8 : 0.5,
                        fontSize: '13px',
                        fontWeight: 500,
                        textTransform: 'none',
                        backgroundColor: theme.palette.primary.grey10,
                        borderColor: '#E4E7EC',
                        color: '#7A7F8C',
                        whiteSpace: 'nowrap',
                        flex: isMobile ? 1 : 'initial',
                        '& .lucide': {
                          width: 16,
                          height: 16,
                        },
                        '&:hover': {
                          borderColor: '#CBD0DA',
                        },
                      }}
                    >
                      삭제
                    </Button>
                    {item.statusLabel === '승인 대기' && (
                      <Button
                        variant="contained"
                        size="small"
                        sx={{
                          backgroundColor: '#06C755',
                          color: '#ffffff',
                          borderRadius: '8px',
                          px: isMobile ? 1.5 : 2,
                          py: isMobile ? 0.8 : 0.5,
                          fontSize: '13px',
                          fontWeight: 600,
                          textTransform: 'none',
                          boxShadow: 'none',
                          flex: isMobile ? 1 : 'initial',
                          whiteSpace: 'nowrap',
                          '&:hover': {
                            backgroundColor: '#05B24D',
                            boxShadow: 'none',
                          },
                        }}
                      >
                        승인
                      </Button>
                    )}
                  </Box>
                </Box>
              </Box>

              {/* 하위 답글들 (메인 댓글의 회색 박스 안에서 렌더링) */}
              {item.replies?.map(reply => (
                <Box
                  key={reply.id}
                  mt={1}
                  display="flex"
                  alignItems="flex-start"
                  gap={isMobile ? 0 : 1}
                >
                  {!isMobile && (
                    <CornerDownRight
                      size={18}
                      color={theme.palette.primary.greytxt}
                      style={{ marginTop: '2px', flexShrink: 0 }}
                    />
                  )}
                  <Box flex={1}>
                    {/* 상단 메타 정보 (체크박스, 상태, 타입, 작성자, 날짜, IP) */}
                    <Box
                      display="flex"
                      alignItems="center"
                      flexWrap="wrap"
                      rowGap={0.8}
                      columnGap={1}
                      mb={0.5}
                    >
                      {/* 하위 댓글 체크박스 */}
                      <Checkbox
                        size="small"
                        sx={{
                          p: 0,
                          mr: 0.5,
                          '&.Mui-checked': {
                            color: theme.palette.primary.mainBlue,
                          },
                        }}
                      />

                      <Box
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.6,
                          backgroundColor:
                            reply.statusLabel === '승인됨'
                              ? '#06C755'
                              : reply.statusLabel === '승인 대기'
                              ? '#D9AA00'
                              : reply.statusLabel === '신고됨'
                              ? '#F04438'
                              : theme.palette.primary.grey10,
                          color: '#ffffff',
                          px: 1.2,
                          py: 0.3,
                          borderRadius: '999px',
                          fontSize: '12px',
                          fontWeight: 600,
                        }}
                      >
                        {reply.statusLabel === '승인됨' && (
                          <Check size={14} strokeWidth={3} />
                        )}
                        {reply.statusLabel}
                      </Box>
                      <Box
                        sx={{
                          backgroundColor: theme.palette.primary.grey10,
                          color: '#4B4F5C',
                          px: 1.2,
                          py: 0.3,
                          borderRadius: '999px',
                          fontSize: '12px',
                          fontWeight: 500,
                        }}
                      >
                        {reply.typeLabel}
                      </Box>

                      <Small
                        sx={{
                          color: '#151515',
                          fontWeight: 500,
                          mr: 0.5,
                          fontSize: '13px',
                        }}
                      >
                        {reply.nickname}
                      </Small>
                      {reply.realName && (
                        <Tiny sx={{ color: theme.palette.primary.greytxt }}>
                          ({reply.realName})
                        </Tiny>
                      )}

                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          ml: 'auto',
                          flexWrap: 'wrap',
                        }}
                      >
                        <Box
                          sx={{
                            backgroundColor: theme.palette.primary.grey10,
                            borderRadius: '999px',
                            px: 1.2,
                            py: 0.3,
                          }}
                        >
                          <Tiny
                            sx={{
                              color: '#4B4F5C',
                              fontWeight: 500,
                              fontSize: '12px',
                            }}
                          >
                            {reply.date}
                          </Tiny>
                        </Box>
                        <Box
                          sx={{
                            backgroundColor: theme.palette.primary.grey10,
                            borderRadius: '999px',
                            px: 1.2,
                            py: 0.3,
                          }}
                        >
                          <Tiny
                            sx={{
                              color: '#4B4F5C',
                              fontWeight: 500,
                              fontSize: '12px',
                            }}
                          >
                            IP : {reply.ip}
                          </Tiny>
                        </Box>
                      </Box>
                    </Box>

                    {/* 하위 답글 본문 + 액션 버튼들 */}
                    <Box
                      sx={{
                        backgroundColor: '#ffffff',
                        borderRadius: '12px',
                        px: 2,
                        py: 1.5,
                        boxShadow: '0px 2px 5px rgba(0, 0, 0, 0.05)',
                      }}
                    >
                      <Small
                        sx={{
                          color: theme.palette.text.primary,
                          lineHeight: 1.5,
                          fontSize: isMobile ? '13px' : '14px',
                        }}
                      >
                        {reply.commentText}
                      </Small>

                      <Box
                        mt={1.5}
                        display="flex"
                        alignItems="center"
                        gap={1}
                        flexWrap="wrap"
                      >
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<Edit3 size={16} />}
                          onClick={e => {
                            e.stopPropagation();
                            handleEditClick(reply, true);
                          }}
                          sx={{
                            backgroundColor: theme.palette.primary.mainBlue,
                            color: '#ffffff',
                            borderRadius: '8px',
                            px: isMobile ? 1.5 : 2,
                            py: isMobile ? 0.8 : 0.5,
                            fontSize: '13px',
                            fontWeight: 600,
                            textTransform: 'none',
                            boxShadow: 'none',
                            flex: isMobile ? 1 : 'initial',
                            whiteSpace: 'nowrap',
                            '& .lucide': {
                              width: 16,
                              height: 16,
                            },
                            '&:hover': {
                              backgroundColor:
                                theme.palette.primary.mainBlueHover,
                              boxShadow: 'none',
                            },
                          }}
                        >
                          수정
                        </Button>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<Trash2 size={16} />}
                          sx={{
                            borderRadius: '8px',
                            px: isMobile ? 1.5 : 2,
                            py: isMobile ? 0.8 : 0.5,
                            fontSize: '13px',
                            fontWeight: 500,
                            textTransform: 'none',
                            backgroundColor: theme.palette.primary.grey10,
                            borderColor: '#E4E7EC',
                            color: '#7A7F8C',
                            flex: isMobile ? 1 : 'initial',
                            whiteSpace: 'nowrap',
                            '& .lucide': {
                              width: 16,
                              height: 16,
                            },
                            '&:hover': {
                              borderColor: '#CBD0DA',
                            },
                          }}
                        >
                          삭제
                        </Button>
                        {reply.statusLabel === '승인 대기' && (
                          <Button
                            variant="contained"
                            size="small"
                            sx={{
                              backgroundColor: '#06C755',
                              color: '#ffffff',
                              borderRadius: '8px',
                              px: isMobile ? 1.5 : 2,
                              py: isMobile ? 0.8 : 0.5,
                              fontSize: '13px',
                              fontWeight: 600,
                              textTransform: 'none',
                              boxShadow: 'none',
                              flex: isMobile ? 1 : 'initial',
                              whiteSpace: 'nowrap',
                              '&:hover': {
                                backgroundColor: '#05B24D',
                                boxShadow: 'none',
                              },
                            }}
                          >
                            승인
                          </Button>
                        )}
                      </Box>
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Grid>

      {/* Edit Comment Modal */}
      <EditCommentModal
        showModal={showEditModal}
        setShowModal={setShowEditModal}
        commentText={editingComment?.commentText || ''}
        onSave={handleSaveComment}
      />
    </>
  );
};

export default CrmCommentsItem;
